import { useEffect } from "react";

/**
 * Warns the user before the browser leaves the page while `when` is true — a tab close, a reload, or
 * a navigation to another site. Use it to guard work that lives only in memory and would be lost:
 * an in-progress edit, or files selected but not yet uploaded.
 *
 * Scope and limits, all imposed by the browser rather than this hook:
 * - It cannot guard **in-app** navigation. A react-router route change never fires `beforeunload`;
 *   block those with react-router's own `useBlocker`.
 * - The message is **not** customizable. Every modern browser shows its own generic wording, so
 *   there is deliberately no message parameter.
 * - Chrome and Firefox suppress the prompt entirely unless the user has interacted with the page
 *   (a click or keypress). Typing into a form satisfies this; merely opening one does not.
 *
 * Because the prompt is advisory and easily suppressed, treat it as a last line of defense. Prefer
 * persisting the work — this hook is for state that genuinely cannot be saved yet.
 *
 * @param when Whether unsaved work currently exists. The listener is attached only while true.
 *
 * @example
 * ```tsx
 * useBlockNavigation(pendingFiles.length > 0)
 * ```
 */
export const useBlockNavigation = (when: boolean): void => {
  // Subscribing to a browser lifecycle event — an external system, not React state.
  useEffect(() => {
    if (!when) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy browsers require returnValue to be set for the prompt to appear; the string
      // itself is ignored in favor of the browser's own wording.
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [when]);
};
